import { useCombobox, type UseComboboxReturnValue } from 'downshift';
import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';

import { actions, reducer } from './reducer.ts';
import { initialState, type SgCurrencyPairPickerState } from './state.ts';

export const SgCurrencyPairPickerContext = createContext<{
  state: SgCurrencyPairPickerState;
  dispatch: Dispatch<Parameters<typeof reducer>[1]>;

  inputRef: HTMLInputElement | null;
  setInputRef: (ref: HTMLInputElement | null) => void;

  comboBox: UseComboboxReturnValue<string>;
  onChange?: (value: string | undefined) => void;
}>({
  state: initialState('', false, []),
  dispatch: () => {},
  comboBox: {} as UseComboboxReturnValue<string>,
  inputRef: null,
  setInputRef: () => {},
  onChange: undefined,
});

export const useSgCurrencyPairPickerContext = () => {
  const context = useContext(SgCurrencyPairPickerContext);

  if (!context) {
    throw new Error('useSgCurrencyPairPickerContext must be used within a SgCurrencyPairPickerProvider');
  }

  return context;
};

export type SgCurrencyPairPickerProviderProps = {
  value: string;
  isRequired: boolean;
  currencyPairs?: string[];
  onChange?: (value: string | undefined) => void;
};

export const SgCurrencyPairPickerProvider = (props: PropsWithChildren<SgCurrencyPairPickerProviderProps>) => {
  const { value, isRequired, currencyPairs, onChange, children } = props;

  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);

  const [state, dispatch] = useReducer(reducer, initialState(value, isRequired, currencyPairs ?? []));

  useEffect(() => {
    dispatch(actions.valueChanged({ value }));
  }, [value]);

  useEffect(() => {
    dispatch(actions.currencyPairsChanged({ currencyPairs: currencyPairs ?? [] }));
  }, [currencyPairs]);

  const comboBox = useCombobox({
    inputValue: state.editedValue,
    items: state.filteredCurrencyPairs,
    defaultHighlightedIndex: 0, // Always highlight first item

    onInputValueChange: ({ inputValue }) => {
      dispatch(actions.valueEdited({ value: inputValue || '' }));
    },

    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        onChange?.(selectedItem);
        dispatch(actions.currencyPairSelected({ currencyPair: selectedItem }));
      }
    },
  });

  return (
    <SgCurrencyPairPickerContext value={{ state, dispatch, comboBox, inputRef, setInputRef, onChange }}>
      {children}
    </SgCurrencyPairPickerContext>
  );
};
